import { FC, type ReactElement } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { VisaoGeralMes } from '@/types/dashboards';
import type { PontoSerie } from '@/utils/equiplexVisaoGeral';
import { fmtCompacto, fmtData, fmtDesvio, fmtDiaMes, fmtPct, fmtUn } from '@/utils/painelFormat';
import { mostrarIndices, ultimoPonto, valoresDe } from '@/utils/rotulosGrafico';
import { ALTURA_GRAFICO, FONTE_EIXO, tickEixo, type CoresPainel } from '../chartTheme';
import { rotuloDoPonto } from '../rotuloBarra';
import Legenda, { type ItemLegenda } from '../Legenda';
import PainelCard from '../PainelCard';
import TooltipBox, { type LinhaTooltip } from '../TooltipBox';

interface Props {
  serie: PontoSerie[];
  categorias: string[];
  selecionadas: string[];
  onAlternar: (categoria: string) => void;
  mes: VisaoGeralMes;
  corte: string;
  cores: CoresPainel;
}

function linhasTooltip(p: PontoSerie, cores: CoresPainel): LinhaTooltip[] {
  const linhas: LinhaTooltip[] = [];
  if (p.realizadoAcum !== null) {
    linhas.push({ rotulo: 'Produzido (acumulado)', valor: fmtUn(p.realizadoAcum), cor: cores.produzido });
  }
  if (p.planoAcum !== null) {
    linhas.push({ rotulo: 'Plano S&OP acumulado', valor: fmtUn(p.planoAcum), cor: cores.programado });
  }
  if (p.realizadoAcum !== null && p.planoAcum) {
    linhas.push({ rotulo: 'Contra o ritmo do plano', valor: fmtDesvio(((p.realizadoAcum - p.planoAcum) / p.planoAcum) * 100) });
  }
  return linhas;
}

function itensLegenda(temPlano: boolean, cores: CoresPainel): ItemLegenda[] {
  const itens: ItemLegenda[] = [{ rotulo: 'Produzido (acumulado)', cor: cores.produzido }];
  if (temPlano) itens.push({ rotulo: 'Plano S&OP acumulado (ritmo linear)', cor: cores.programado, tracejado: true });
  return itens;
}

/** "4,2 mi · 88% do plano": o fim da linha do produzido diz onde a fábrica está. */
function fmtRealizadoNoFim(serie: PontoSerie[]) {
  return (v: number, indice: number): string => {
    const plano = serie[indice]?.planoAcum;
    return plano ? `${fmtCompacto(v)} · ${fmtPct((v / plano) * 100)} do plano` : fmtCompacto(v);
  };
}

interface OpcoesGrafico {
  serie: PontoSerie[];
  temPlano: boolean;
  corte: string;
  corteNoMes: boolean;
  cores: CoresPainel;
  conteudoTooltip: (props: TooltipContentProps) => ReactElement | null;
}

/** Filhos do `ComposedChart` como lista (o Recharts procura os filhos diretos por tipo). */
function elementosDoGrafico({ serie, temPlano, corte, corteNoMes, cores, conteudoTooltip }: OpcoesGrafico): ReactElement[] {
  const elementos: ReactElement[] = [
    <CartesianGrid key="grade" vertical={false} stroke={cores.grade} />,
    <XAxis
      key="x"
      dataKey="data"
      tickFormatter={fmtDiaMes}
      tick={tickEixo(cores)}
      axisLine={{ stroke: cores.eixo }}
      tickLine={false}
      minTickGap={20}
    />,
    <YAxis key="y" tickFormatter={fmtCompacto} tick={tickEixo(cores)} axisLine={false} tickLine={false} width={64} />,
    <Tooltip key="tooltip" content={conteudoTooltip} cursor={{ stroke: cores.textoMuted, strokeWidth: 1 }} />,
  ];
  if (corteNoMes) {
    elementos.push(
      <ReferenceLine
        key="corte"
        x={corte}
        stroke={cores.textoMuted}
        strokeDasharray="3 3"
        label={{ value: `reportado até ${fmtDiaMes(corte)}`, position: 'insideTopRight', fill: cores.textoMuted, fontSize: FONTE_EIXO - 1 }}
      />,
    );
  }
  if (temPlano) {
    elementos.push(
      <Line
        key="plano"
        type="linear"
        dataKey="planoAcum"
        name="Plano S&OP"
        stroke={cores.programado}
        strokeWidth={2}
        strokeDasharray="6 4"
        dot={false}
        activeDot={false}
        isAnimationActive={false}
      >
        <LabelList
          dataKey="planoAcum"
          content={rotuloDoPonto((v) => `plano ${fmtCompacto(v)}`, cores.texto, mostrarIndices(ultimoPonto(valoresDe(serie, 'planoAcum')), false))}
        />
      </Line>,
    );
  }
  elementos.push(
    <Line
      key="realizado"
      type="linear"
      dataKey="realizadoAcum"
      name="Produzido (acumulado)"
      stroke={cores.produzido}
      strokeWidth={2.5}
      dot={false}
      activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--bg-card)' }}
      connectNulls={false}
      isAnimationActive={false}
    >
      <LabelList
        dataKey="realizadoAcum"
        content={rotuloDoPonto(fmtRealizadoNoFim(serie), cores.texto, mostrarIndices(ultimoPonto(valoresDe(serie, 'realizadoAcum')), false))}
      />
    </Line>,
  );
  return elementos;
}

/** Chips de formato: o recorte vale para este gráfico, para os KPIs e para a tabela por SKU. */
const Formatos: FC<Pick<Props, 'categorias' | 'selecionadas' | 'onAlternar'>> = ({ categorias, selecionadas, onAlternar }) => (
  <div role="group" aria-label="Formatos no total" className="flex flex-wrap items-center gap-1.5">
    <span className="mr-1 text-xs uppercase tracking-wide text-content-muted">Formatos no total</span>
    {categorias.map((c) => {
      const ativa = selecionadas.includes(c);
      return (
        <button
          key={c}
          type="button"
          aria-pressed={ativa}
          onClick={() => onAlternar(c)}
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
            ativa
              ? 'border-theme-border bg-surface-hover font-medium text-content-primary'
              : 'border-transparent text-content-muted hover:text-content-primary'
          }`}
        >
          {ativa && (
            <span aria-hidden className="material-symbols-outlined text-[14px] leading-none">
              check
            </span>
          )}
          {c}
        </button>
      );
    })}
  </div>
);

/**
 * S&OP × realizado no acumulado do mês: a linha do plano sobe em ritmo linear até o total
 * do mês e a do realizado mostra onde a fábrica está — a distância entre as duas é o que
 * falta para fechar. O realizado para no corte da réplica; depois dele não há dado.
 */
const SopRealizadoChart: FC<Props> = ({ serie, categorias, selecionadas, onAlternar, mes, corte, cores }) => {
  const temPlano = serie.some((p) => p.planoAcum !== null);
  const corteNoMes = mes.em_andamento && corte >= mes.start && corte < mes.end;

  const conteudoTooltip = ({ active, payload, label }: TooltipContentProps) => {
    const p = active && payload?.[0] ? (payload[0].payload as PontoSerie) : null;
    if (!p) return null;
    const rodape = p.realizado === null ? 'Depois do corte: a réplica ainda não tem este dia.' : undefined;
    return <TooltipBox titulo={fmtData(String(label))} linhas={linhasTooltip(p, cores)} rodape={rodape} />;
  };

  return (
    <PainelCard
      testId="vg-chart-sop"
      titulo="S&OP × realizado no mês (acumulado)"
      subtitulo={`${mes.label} (${mes.range}). Vamos fechar o plano? Total do mês contra o ritmo do S&OP${
        mes.em_andamento ? `, até ${fmtData(corte)}` : ''
      }.`}
    >
      <Formatos categorias={categorias} selecionadas={selecionadas} onAlternar={onAlternar} />
      <Legenda itens={itensLegenda(temPlano, cores)} />
      <div style={{ width: '100%', height: ALTURA_GRAFICO }}>
        {selecionadas.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-content-muted">Escolha ao menos um formato.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={serie} margin={{ top: 24, right: 40, bottom: 0, left: 0 }}>
              {elementosDoGrafico({ serie, temPlano, corte, corteNoMes, cores, conteudoTooltip })}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </PainelCard>
  );
};

export default SopRealizadoChart;
